import React, { useEffect, useState } from 'react'
import SideBar from '../Sidebar/SideBar';
import AppReleaseServerApi from '../../Api/AppReleaseServerApi';
import { Icon } from '@iconify/react/dist/iconify.js';
import Swal from "sweetalert2";
import Loader from '../Loader';

const AppReleaseServer = () => {

    /* 
    "name": "Version 1.0",
  "vdate": "2024-09-16",
  "remark": "Initial Release",
  "release": "release_1.0.zip",
  "filepath": "/path/to/file"
    */
    const today = () => new Date().toISOString().slice(0, 10);
    const emptyForm = () => ({
        name: '',
        filepath: '',
        vdate: today(),
        remark: '',
        type: 0,
        release: null,
    });

    const [data, setData] = useState([]);
    const [itemId, setItemId] = useState('');

    const [inputValues, setInputValues] = useState(emptyForm());
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const formatIsoDate = (isoDate) => {
        if (!isoDate) return '';
        const d = new Date(isoDate);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    };

    const formatDisplayDate = (value) => {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-GB');
    };


    const handleChange = (e) => {
        const { name, value, files } = e.target;

        // If the input is a file input, set the file instead of the value
        if (name === "release") {
            setInputValues((prevValues) => ({
                ...prevValues,
                [name]: files[0], // Store the file object
            }));
        } else if (name === "type") {
            setInputValues((prevValues) => ({
                ...prevValues,
                type: Number(value),
            }));
        } else {
            setInputValues((prevValues) => ({
                ...prevValues,
                [name]: value,
            }));
        }
    };

    console.log("inputValues", inputValues);

    const handleSave = async () => {
        if (!inputValues.name || !inputValues.vdate || !inputValues.release) {
            Swal.fire({ title: "Info", text: "Name, Date and a file are required", icon: "info", width: "320px" });
            return;
        }
        if (!(inputValues.release instanceof File)) {
            Swal.fire({ title: "Info", text: "Please choose a file to upload.", icon: "info", width: "320px" });
            return;
        }

        const formData = new FormData();
        formData.append('name', inputValues.name);
        formData.append('vdate', inputValues.vdate);
        formData.append('remark', inputValues.remark);
        formData.append('type', String(inputValues.type ?? 0));
        formData.append('file', inputValues.release);

        setLoading(true);
        try {
            const response = await AppReleaseServerApi.CreateAppReleaseServer(formData)
            if (response?.StatusCode === 201) {
                setInputValues(emptyForm())
                fetchData()
                Swal.fire({ title: "Saved", text: "Release created.", icon: "success", width: "320px" })
            } else {
                Swal.fire({ title: "Error", text: response?.ErrorMessages || "Save failed", icon: "error", width: "320px" })
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {

        if (!itemId) return;
        if (!inputValues.name || !inputValues.vdate) {
            Swal.fire({ title: "Info", text: "Name and Date are required", icon: "info", width: "320px" });
            return;
        }

        const formData = new FormData();
        formData.append('name', inputValues.name);
        formData.append('vdate', inputValues.vdate);
        formData.append('remark', inputValues.remark);
        formData.append('type', String(inputValues.type ?? 0));
        if (inputValues.release instanceof File) {
            formData.append('file', inputValues.release);
        }

        setLoading(true);
        try {
            const response = await AppReleaseServerApi.UpdateAppReleaseServer(itemId, formData)
            if (response?.StatusCode === 200) {
                fetchData()
                setInputValues(emptyForm())
                setItemId('')
                Swal.fire({ title: "Updated", text: "Release updated.", icon: "success", width: "320px" })
            } else {
                Swal.fire({ title: "Error", text: response?.ErrorMessages || "Update failed", icon: "error", width: "320px" })
            }
        } finally {
            setLoading(false);
        }
    }

    const handleEdit = (item) => {
        console.log("item", item);
        setItemId(item.id)
        const formattedDate = formatIsoDate(item.vdate)
        setInputValues({
            name: item.name,
            filepath: item.filepath,
            vdate: formattedDate,
            remark: item.remark,
            type: Number(item.type) === 1 ? 1 : 0,
            release: null,
        })

        console.log(`Edit item with id ${item.id}`);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Delete this release?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
            cancelButtonText: "No, keep it",
            reverseButtons: true,
            width: "340px",
        });

        if (!result.isConfirmed) return;

        const response = await AppReleaseServerApi.DeleteAppReleaseServer(id);
        if (response.StatusCode === 200) {
            Swal.fire({ title: "Deleted", text: "Deleted successfully", icon: "success", width: "320px" })
            fetchData()
        }
    };

    const handleDownload = async (item) => {
        setDownloadingId(item.id);
        try {
            const resp = await AppReleaseServerApi.DownloadRelease(item.release);
            const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = item.release || 'release.zip';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            Swal.fire({ title: "Error", text: err?.response?.data?.ErrorMessages || "Download failed", icon: "error", width: "320px" });
        } finally {
            setDownloadingId(null);
        }
    };

    const fetchData = async () => {
        const response = await AppReleaseServerApi.GetAppReleaseServer()
        if (response) {
            console.log("response", response);
            setData(response.Result)
        }
    }

    useEffect(() => {
        fetchData();
    }, []);


    return (
        <div className='Flex-general-client-sidebar'>
            <div className='sidebar-gen-width'>
                <SideBar />
            </div>
            <div className='Main-general-Client'>
                <div className='header-general'>
                    <h2 className='general-card-title'>App Release Server</h2>
                </div>
                {loading && <Loader />}
                <div className="control_radius">
                    {/* <div className="company_info-text-im depart_top">Report Template Setting</div> */}
                    <div className="company_control">
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1">
                                <div className="input-box-mok1">
                                    <input className="modify_im_input mode_category1"
                                        type="text" name="name" onChange={handleChange} value={inputValues.name} required />
                                    <label class="modify_p_text2">Name</label>
                                </div>
                            </div>
                        </div>
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1 input-height2">
                                <div className="input-box-mok1">
                                    <select
                                        className="modify_im_input mode_category1"
                                        name="type"
                                        value={inputValues.type}
                                        onChange={handleChange}
                                    >
                                        <option value={0}>App</option>
                                        <option value={1}>Query</option>
                                    </select>
                                    <label className="modify_p_text2">Type</label>
                                </div>
                            </div>
                        </div>
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1 input-height2">
                                <div className="input-box-mok1">
                                    <input className="modify_im_input mode_category1"
                                        type="date" name="vdate" value={inputValues.vdate} onChange={handleChange} required />
                                    <label class="modify_lbl_text3">V Date</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="company_control">
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1 input-height2">
                                <div className="input-box-mok1">
                                    <label class="modify_lbl_text3">Release</label>
                                    <input className="modify_im_input mode_category1"
                                        type="file" name="release" onChange={handleChange} required />
                                </div>
                            </div>
                        </div>
                        <div className="stock-im-dep1">
                            <div className="input-box-Imo1 input-height2">
                                <div className="input-box-mok1">
                                    <textarea className="modify_im_input mode_category1"
                                        type="text" name="remark" onChange={handleChange} value={inputValues.remark}></ textarea>
                                    <label class="modify_p_text2">Remark</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="ButtonTabSave">
                        {
                            itemId ? (
                                <button
                                    id="SaveBtnMain"
                                    className={`update-button-main-save hovered`}
                                    onClick={handleUpdate}
                                >
                                    Update
                                </button>
                            ) : (
                                <button
                                    id="SaveBtnMain"
                                    className={`save-button-main-save hovered`}
                                    onClick={handleSave}
                                >
                                    Save
                                </button>
                            )
                        }
                    </div>

                    <div className="company_info-text-im depart_top">App Release Server List</div>
                    <div className="control_radius">
                        <div className="table-account-manager">
                            <table className='table-master-acc'>
                                <thead className='header-table-account'>
                                    <tr>
                                        <th className='table-header-manager ac-sno-wth'>S.No</th>
                                        {/* <th className='table-header-manager'>Id</th> */}
                                        <th className='table-header-manager'>Name</th>
                                        <th className='table-header-manager'>Type</th>
                                        <th className='table-header-manager'>V Date</th>
                                        <th className='table-header-manager'>Remark</th>
                                        <th className='table-header-manager'>Release File Name</th>
                                        <th className='table-header-manager'>Download</th>
                                        <th className='table-header-manager'>Creation Date</th>
                                        <th className='table-header-manager'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item, idx) => (
                                        <tr key={item.id}>
                                            <td className='table-data-account ac-sno-wth'>{idx + 1}</td>
                                            {/* <td className='table-data-account'>{item.id}</td> */}
                                            <td className='table-data-account'>{item.name}</td>
                                            <td className='table-data-account'>{Number(item.type) === 1 ? 'Query' : 'App'}</td>
                                            <td className='table-data-account'>{formatDisplayDate(item.vdate)}</td>
                                            <td className='table-data-account'>{item.remark}</td>
                                            <td className='table-data-account'>{item.release}</td>
                                            <td className='table-data-account'>
                                                <button className="edit-btn-acc" disabled={downloadingId === item.id} onClick={() => handleDownload(item)}>
                                                    {downloadingId === item.id ? "Downloading..." : "Download"}
                                                </button>
                                            </td>
                                            <td className='table-data-account'>{formatDisplayDate(item.cr_dt)}</td>
                                            <td className='table-data-account'>
                                                <button className="edit-btn-acc" onClick={() => handleEdit(item)}><Icon icon="lucide:edit" /></button>
                                                <button className="delete-btn-acc" onClick={() => handleDelete(item.id)}><Icon icon="material-symbols:delete" /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AppReleaseServer;
